import React, { useCallback, useMemo } from 'react';
import Helmet from 'react-helmet';
import { matchPath, Route, Switch, useHistory, useLocation } from 'react-router';
import styled, { css } from 'styled-components';
// schemas and utils
import { ExemptionCalculatorSteps, fullRoutes, Routes } from 'constants/index';
import { useCIMARehydrate } from 'hooks/useCIMARehydrate';

import { getPath } from 'utils';
// components
import { Link, SecureRoute } from 'components/atoms';
import { HeaderCimaMembership } from 'components/molecules';
import { MiniUserWidgetContainer } from 'containers/UserWidgetContainer';
import { CimaExemptionEducationalFormContainer } from 'containers/CimaMembershipStudypath/CimaExemptionEducationalFormContainer';
import { CimaExemptionProfessionalFormContainer } from 'containers/CimaMembershipStudypath/CimaExemptionProfessionalFormContainer';
import { CimaExemptionWorkFormContainer } from 'containers/CimaMembershipStudypath/CimaExemptionWorkFormContainer';
import { ReactComponent as AicpaCimaLogoWhite } from 'resources/images/aicpa-cima-logo-white.svg';
import { ReactComponent as ArrowBack } from 'resources/images/ic-arrow-back.svg';
import { Loader } from 'semantic-ui-react';
import { ExemptionCalculatorSummary } from '../PageExemptionCalculatorSummary';
import { ExemptionCalculatorResult } from '../PageExemptionCalculatorResult';

interface Props {
  isAuth: boolean | null;
}

const CimaExemptionCalculatorForms: Record<string, React.FC> = {
  [Routes.EXEMPTION_CALCULATOR_PROFESSIONAL]: CimaExemptionProfessionalFormContainer,
  [Routes.EXEMPTION_CALCULATOR_EDUCATIONAL]: CimaExemptionEducationalFormContainer,
  [Routes.EXEMPTION_CALCULATOR_WORK]: CimaExemptionWorkFormContainer,
  [Routes.EXEMPTION_CALCULATOR_SUMMARY]: ExemptionCalculatorSummary,
};

export const PageCimaExemptionCalculator: React.FC<Props> = ({ isAuth }) => {
  const history = useHistory();

  const { pathname: currentPath } = useLocation();

  const isFetchingDataNeededFromCIMA = useCIMARehydrate();

  const backRoute = useMemo(() => {
    const indexOfCurrentPath = ExemptionCalculatorSteps.findIndex(({ pathName }) =>
      matchPath(currentPath, getPath(pathName))
    );

    const prevStep =
      indexOfCurrentPath !== 0 ? ExemptionCalculatorSteps[indexOfCurrentPath - 1]?.pathName : Routes.FEED;
    return getPath(prevStep);
  }, [currentPath]);

  const isResultPage = currentPath === getPath(Routes.EXEMPTION_CALCULATOR_RESULT);

  const handleLogoClick = useCallback(() => {
    history.push(getPath(Routes.ROOT));
  }, [history]);

  const softCopy: any = {
    [fullRoutes[Routes.EXEMPTION_CALCULATOR_PROFESSIONAL].path]:
      'We value your experience, and exemptions could be applied for previously received professional qualifications.',
    [fullRoutes[Routes.CIMA_MEMBERSHIP_STUDYPATH_EXEMPTIONS_EDUCATIONAL].path]:
      'We value education, and we ask because you may qualify for exemptions. ',
    [fullRoutes[Routes.EXEMPTION_CALCULATOR_WORK].path]:
      'We ask because work experience is noteworthy and may qualify you for exemptions.',
  };

  return (
    <>
      {!isFetchingDataNeededFromCIMA ? (
        <>
          <Helmet>
            <title>Exemption Calculator</title>
          </Helmet>
          <StyledNav>
            <StyledNavContainer>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px' }}>
                <StyledAicpaCimaLogoWhite onClick={handleLogoClick} />
                {isAuth && <MiniUserWidgetContainer showLogoutLink={true} isWhite={true} />}
              </div>
              {!isResultPage && (
                <div style={{ position: 'relative', padding: '0 20px' }}>
                  <StyledBackLink to={backRoute}>
                    <ArrowBack /> Back
                  </StyledBackLink>
                </div>
              )}
              {!isResultPage && <HeaderCimaMembership />}
            </StyledNavContainer>
          </StyledNav>
          <StyledBody>
            <p style={{ textAlign: 'center' }}>{softCopy[currentPath]}</p>
            <Switch>
              {ExemptionCalculatorSteps.map(({ pathName, name }) => {
                return (
                  <Route
                    path={getPath(pathName)}
                    key={`page-${name}`}
                    component={CimaExemptionCalculatorForms[pathName]}
                  />
                );
              })}
              <SecureRoute isAuth={isAuth} exact component={ExemptionCalculatorResult} />
            </Switch>
          </StyledBody>
        </>
      ) : (
        <Loader active content="Loading" />
      )}
    </>
  );
};

const BaseStyles = css`
  max-width: 930px;
  margin: auto;
  line-height: 1.5;
  width: 100%;
`;

const StyledNav = styled.div`
  background-image: linear-gradient(73deg, #6a2d87 35%, #a93d69 101%);
  border-radius: 0 0 65% 65%/0 0 100% 100%;
  line-height: 1.5;
  height: auto;

  ${({ theme }) => theme.mediaQueries.mobileOnly} {
    border-radius: 0 0 100% 100%/0 0 100% 100%;
    height: 357px;
  }
`;

const StyledNavContainer = styled.div`
  overflow-x: clip;
  max-width: 1440px;
  margin: auto;
  position: relative;
  ${({ theme }) => theme.mediaQueries.mobileOnly} {
    height: 365px;
  }
`;

const StyledAicpaCimaLogoWhite = styled(AicpaCimaLogoWhite)`
  cursor: pointer;
`;

const StyledBody = styled.div`
  ${BaseStyles}
  padding-top: 24px;
  color: ${props => props.theme.colors.neutralGrey8};
  font-size: ${props => props.theme.pxToRem(16)};
  height: 750px;

  ${({ theme }) => theme.mediaQueries.mobileOnly} {
    padding: 64px 20px;
    margin: 0px;
  }
`;

const StyledBackLink = styled(Link)`
  display: inline-flex;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-decoration: none;
  color: ${props => props.theme.colors.neutralWhite} !important;
  margin: ${props => `${props.theme.pxToRem(25)} 0 ${props.theme.pxToRem(-50)} 0`};
  > svg {
    width: ${props => props.theme.pxToRem(18)};
    height: ${props => props.theme.pxToRem(18)};
    margin-right: ${props => props.theme.pxToRem(4)};
    path {
      fill: ${props => props.theme.colors.neutralWhite};
    }
  }
  ${props => props.theme.mediaQueries.mobileOnly} {
    margin: 0;
  }
`;
